import { useState, useEffect, useRef, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
  AdjustmentsHorizontalIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { Listbox } from "components/shared/form/Listbox";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { updateJobManualStatus, getJobStatuses, upsertJobStatus, bulkJobAction, deleteJobStatus, getJobById } from "services/jobsService";
import { getCompanyIdFromToken, getUserIdFromToken } from "utils/tokenUtils";
import { toast } from "sonner";
import { canManageJobStatus } from "permissions/permissionHelpers/jobPermissions";
import { UpdateLeadStageModal } from "./UpdateLeadStageModal";

// ---------------------------------------------------------------------------
// Static fallback
// ---------------------------------------------------------------------------
const FALLBACK_STATUSES = [
  { value: 1, label: "Pending",            alias: "pending",           type: "System" },
  { value: 5, label: "Under Approval",     alias: "under_approval",    type: "System" },
  { value: 2, label: "Ongoing",            alias: "ongoing",           type: "System" },
  { value: 3, label: "Completed/Warranty", alias: "completed_warranty",type: "System" },
  { value: 4, label: "Archive",            alias: "archive",           type: "System" },
  { value: 6, label: "Assess Queue",       alias: "assess_queue",      type: "Custom" },
];

// Pill badge for System / Custom
function TypeBadge({ type }) {
  const isSystem = type === "System";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ${
        isSystem
          ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
          : "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400"
      }`}
    >
      {type}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Add Status Modal
// ---------------------------------------------------------------------------
function AddStatusModal({ show, onClose, onAdd }) {
  const [name, setName]   = useState("");
  const [alias, setAlias] = useState("");
  const [type, setType]   = useState("Custom");
  const [aliasEdited, setAliasEdited] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    if (show) {
      setName(""); setAlias(""); setType("Custom"); setAliasEdited(false);
      setTimeout(() => nameRef.current?.focus(), 80);
    }
  }, [show]);

  const handleNameChange = (v) => {
    setName(v);
    if (!aliasEdited) setAlias(v);
  };

  const handleAdd = () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    onAdd({ label: name.trim(), alias: alias.trim() || name.trim(), type });
    onClose();
  };

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[10300] flex items-center justify-center px-4" onClose={onClose}>
        <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
        </TransitionChild>
        <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
          <DialogPanel className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-dark-700 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-dark-600 bg-gray-50 dark:bg-dark-800">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/20">
                  <PlusIcon className="w-4 h-4 text-primary dark:text-primary-400" />
                </div>
                <DialogTitle as="h3" className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Add Status
                </DialogTitle>
              </div>
              <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-600 dark:hover:text-white transition-colors">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5 space-y-4">
              {/* Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600 dark:text-dark-300 uppercase tracking-wide">Name <span className="text-error">*</span></label>
                <input
                  ref={nameRef}
                  type="text"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. In Review"
                  className="w-full rounded-lg border border-gray-200 dark:border-dark-500 bg-white dark:bg-dark-600 px-3 py-2 text-sm text-gray-800 dark:text-dark-100 placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary dark:focus:border-primary-400 transition"
                />
              </div>
              {/* Alias */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600 dark:text-dark-300 uppercase tracking-wide">Alias</label>
                <input
                  type="text"
                  value={alias}
                  onChange={(e) => { 
                    const val = e.target.value;
                    setAlias(val); 
                    setAliasEdited(val.length > 0); 
                  }}
                  placeholder="Auto-generated from name"
                  className="w-full rounded-lg border border-gray-200 dark:border-dark-500 bg-white dark:bg-dark-600 px-3 py-2 text-sm text-gray-800 dark:text-dark-100 placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary dark:focus:border-primary-400 transition"
                />
                <p className="text-[10px] text-gray-400 dark:text-dark-400">Auto-filled from name. You can edit it.</p>
              </div>
              
                
             
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-800/50">
              <Button variant="outlined" onClick={onClose} className="px-4 py-2 text-xs font-bold">Cancel</Button>
              <Button color="primary" onClick={handleAdd} className="px-4 py-2 text-xs font-bold">Add Status</Button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

// ---------------------------------------------------------------------------
// Delete Confirm Popup (inside Manage modal) — uses a proper HUI Dialog
// ---------------------------------------------------------------------------
function DeleteStatusPopup({ status, allStatuses, onClose, onConfirm }) {
  const leadCount = status?.jobCount || 0;
  const [replaceWith, setReplaceWith] = useState("");
  const replacementOptions = allStatuses.filter((s) => s.value !== status?.value);

  return (
    <Transition appear show={!!status} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[10300] flex items-center justify-center px-4"
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
          leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-dark-700 shadow-2xl overflow-visible">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-dark-600 bg-red-50 dark:bg-red-900/20 rounded-t-2xl">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                  <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Delete Status</span>
              </div>
              <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 transition-colors">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5 space-y-4">
              <p className="text-sm text-gray-700 dark:text-dark-200">
                Are you sure you want to delete{" "}
                <span className="font-bold text-gray-900 dark:text-white">"{status?.alias || status?.label}"</span>?
              </p>
              {leadCount > 0 && (
                <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 px-3 py-2.5">
                  <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    There are currently <span className="font-bold">{leadCount} jobs</span> assigned this status.
                  </p>
                </div>
              )}

              {/* Replace with dropdown (only if jobs exist) */}
              {leadCount > 0 && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-600 dark:text-dark-300 uppercase tracking-wide">
                    Replace jobs with status
                  </label>
                  <Listbox
                    data={replacementOptions}
                    value={replaceWith}
                    onChange={(selected) => setReplaceWith(selected?.value || "")}
                    placeholder="— Select replacement status —"
                    className="w-full"
                    usePortal={false}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-800/50 rounded-b-2xl">
              <Button variant="outlined" onClick={onClose} className="px-4 py-2 text-xs font-bold">Cancel</Button>
              <Button
                color="error"
                onClick={() => onConfirm(replaceWith)}
                className="px-4 py-2 text-xs font-bold"
                disabled={leadCount > 0 && !replaceWith}
              >
                {leadCount > 0 ? "Replace & Delete" : "Delete"}
              </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
  );
}

// ---------------------------------------------------------------------------
// Manage Status Content
// ---------------------------------------------------------------------------
export function ManageStatusContent({ onClose, statuses, setStatuses, reloadStatuses }) {
  const [showAdd, setShowAdd]           = useState(false);
  const [editingId, setEditingId]       = useState(null); // value of row being edited
  const [editName, setEditName]         = useState("");
  const [editAlias, setEditAlias]       = useState("");
  const [editAliasEdited, setEditAliasEdited] = useState(true);
  const [deletingStatus, setDeletingStatus] = useState(null);
  const nameInputRef  = useRef(null);
  const aliasInputRef = useRef(null);

  const startEdit = (status) => {
    setEditingId(status.value);
    setEditName(status.label);
    setEditAlias(status.alias);
    setEditAliasEdited(true);
    // focus on next tick
    setTimeout(() => {
      if (status.type === "System") aliasInputRef.current?.focus();
      else nameInputRef.current?.focus();
    }, 60);
  };

  const cancelEdit = () => { setEditingId(null); setEditName(""); setEditAlias(""); setEditAliasEdited(true); };

  const saveEdit = async (status) => {
    if (!editName.trim()) { toast.error("Name cannot be empty"); return; }
    
    const payload = {
      statusId: status.value,
      statusName: editName.trim(),
      alias: editAlias.trim() || editName.trim(),
      companyId: getCompanyIdFromToken(),
      userId: getUserIdFromToken()
    };

    try {
      const res = await upsertJobStatus(payload);
      if (res.success) {
        toast.success("Status updated successfully");
        cancelEdit();
        if (reloadStatuses) reloadStatuses(true);
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (err) {
      toast.error("An error occurred while updating the status.");
    }
  };

  const handleAdd = async (newStatus) => {
    const payload = {
      statusId: 0,
      statusName: newStatus.label,
      alias: newStatus.alias,
      companyId: getCompanyIdFromToken(),
      userId: getUserIdFromToken()
    };

    try {
      const res = await upsertJobStatus(payload);
      if (res.success) {
        toast.success("Status added successfully");
        setShowAdd(false);
        if (reloadStatuses) reloadStatuses(true);
      } else {
        toast.error(res.message || "Failed to add status");
      }
    } catch (err) {
      toast.error("An error occurred while adding the status.");
    }
  };

  const handleDeleteConfirm = async (replaceWithId) => {
    try {
      const statusIdToDelete = deletingStatus?.value;
      const jobCount = deletingStatus?.jobCount || 0;

      if (jobCount > 0) {
        if (!replaceWithId) {
          toast.error("Please select a replacement status first.");
          return;
        }
        
        // 1. Hit bulk action API to reassign jobs first
        const bulkPayload = {
          actionType: 2, // 2 = change status
          jobIds: [], 
          includeAllFiltered: true,
          targetStatusId: replaceWithId,
          companyId: getCompanyIdFromToken(),
          userId: getUserIdFromToken(),
          searchText: "",
          hideArchiveJobs: false,
          statusIds: [statusIdToDelete],
          filterGroupId: null,
          groupedFilters: null
        };
        
        const bulkRes = await bulkJobAction(bulkPayload);
        if (!bulkRes.success) {
          toast.error(bulkRes.message || "Failed to reassign jobs.");
          return;
        }
      }

      // 2. Delete the status itself
      const delRes = await deleteJobStatus(statusIdToDelete);
      if (delRes.success) {
        toast.success("Status deleted successfully");
        setDeletingStatus(null);
        
        if (jobCount > 0) {
          setTimeout(() => {
            window.location.reload();
          }, 800);
        } else {
          if (reloadStatuses) reloadStatuses(true);
        }
      } else {
        toast.error(delRes.message || "Failed to delete status.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while deleting the status.");
    }
  };

  return (
    <>
      <div className="flex flex-col h-[85vh] sm:h-[600px] bg-white dark:bg-dark-700">
        {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-dark-600 bg-gray-50 dark:bg-dark-800 shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/20">
                    <AdjustmentsHorizontalIcon className="w-4 h-4 text-primary dark:text-primary-400" />
                  </div>
                  <DialogTitle as="h3" className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                    Manage Statuses
                  </DialogTitle>
                </div>
                <div className="flex items-center gap-2">
                  {/* Add Status Button */}
                  <button
                    onClick={() => setShowAdd(true)}
                    className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-600 active:scale-95 transition-all shadow-sm"
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                    Add Status
                  </button>
                  <button
                    onClick={onClose}
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-600 dark:hover:text-white transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-y-auto scrollbar-sm flex-1">
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-50 dark:bg-dark-800 border-b border-gray-100 dark:border-dark-600">
                      <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 dark:text-dark-400 uppercase tracking-wider w-[35%]">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 dark:text-dark-400 uppercase tracking-wider w-[30%]">Alias</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 dark:text-dark-400 uppercase tracking-wider w-[15%]">Type</th>
                      <th className="text-right px-6 py-3 text-xs font-bold text-gray-500 dark:text-dark-400 uppercase tracking-wider w-[20%]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-dark-600">
                    {statuses.map((status) => {
                      const isEditing = editingId === status.value;
                      const isCustom  = status.type === "Custom";

                      return (
                        <tr
                          key={status.value}
                          className={`group transition-colors ${
                            isEditing
                              ? "bg-primary-50/40 dark:bg-primary-900/10"
                              : "hover:bg-gray-50/70 dark:hover:bg-dark-800/50"
                          }`}
                        >
                          {/* Name cell */}
                          <td className="px-6 py-3">
                            {isEditing && isCustom ? (
                              <input
                                ref={nameInputRef}
                                type="text"
                                value={editName}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setEditName(val);
                                  if (!editAliasEdited) setEditAlias(val);
                                }}
                                className="w-full rounded-md border border-primary/40 bg-white dark:bg-dark-600 px-2.5 py-1.5 text-sm text-gray-800 dark:text-dark-100 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                              />
                            ) : (
                              <span className="font-semibold text-gray-800 dark:text-dark-100">
                                {isEditing ? editName : status.label}
                              </span>
                            )}
                          </td>

                          {/* Alias cell */}
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <input
                                ref={isEditing && !isCustom ? aliasInputRef : undefined}
                                type="text"
                                value={editAlias}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setEditAlias(val);
                                  setEditAliasEdited(val.length > 0);
                                }}
                                className="w-full rounded-md border border-primary/40 bg-white dark:bg-dark-600 px-2.5 py-1.5 text-sm text-gray-800 dark:text-dark-100 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                              />
                            ) : (
                              <code className="text-xs text-gray-500 dark:text-dark-400 bg-gray-100 dark:bg-dark-600 px-2 py-0.5 rounded font-mono">
                                {status.alias}
                              </code>
                            )}
                          </td>

                          {/* Type cell */}
                          <td className="px-4 py-3">
                            <TypeBadge type={status.type} />
                          </td>

                          {/* Actions cell */}
                          <td className="px-6 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {isEditing ? (
                                <>
                                  {/* Save */}
                                  <div className="relative group/tip">
                                    <button
                                      onClick={() => saveEdit(status)}
                                      className="flex items-center justify-center w-7 h-7 rounded-md bg-primary text-white hover:bg-primary-600 active:scale-95 transition-all"
                                    >
                                      <CheckIcon className="w-3.5 h-3.5" />
                                    </button>
                                    <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 dark:bg-dark-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                                      Save
                                    </span>
                                  </div>
                                  {/* Cancel */}
                                  <div className="relative group/tip">
                                    <button
                                      onClick={cancelEdit}
                                      className="flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-dark-500 text-gray-500 dark:text-dark-400 hover:bg-gray-100 dark:hover:bg-dark-600 active:scale-95 transition-all"
                                    >
                                      <XMarkIcon className="w-3.5 h-3.5" />
                                    </button>
                                    <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 dark:bg-dark-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                                      Cancel
                                    </span>
                                  </div>
                                </>
                              ) : isCustom ? (
                                // Custom: Edit icon + Delete icon
                                <>
                                  {/* Edit */}
                                  <div className="relative group/tip">
                                    <button
                                      onClick={() => startEdit(status)}
                                      className="flex items-center justify-center w-7 h-7 rounded-md text-primary dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 active:scale-95 transition-all"
                                    >
                                      <PencilSquareIcon className="w-4 h-4" />
                                    </button>
                                    <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 dark:bg-dark-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                                      Edit
                                    </span>
                                  </div>
                                  {/* Delete */}
                                  <div className="relative group/tip">
                                    <button
                                      onClick={() => setDeletingStatus(status)}
                                      className="flex items-center justify-center w-7 h-7 rounded-md text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 active:scale-95 transition-all"
                                    >
                                      <TrashIcon className="w-4 h-4" />
                                    </button>
                                    <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 dark:bg-dark-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                                      Delete
                                    </span>
                                  </div>
                                </>
                              ) : (
                                // System: Edit Alias icon only
                                <div className="relative group/tip">
                                  <button
                                    onClick={() => startEdit(status)}
                                    className="flex items-center justify-center w-7 h-7 rounded-md text-primary dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 active:scale-95 transition-all"
                                  >
                                    <PencilSquareIcon className="w-4 h-4" />
                                  </button>
                                  <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 dark:bg-dark-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                                    Edit Alias
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-800/50 shrink-0">
                <Button variant="outlined" onClick={onClose} className="px-4 py-2 text-xs font-bold">Close</Button>
              </div>
      </div>

      {/* Add Status Modal */}
      <AddStatusModal show={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />

      {/* Delete Status Popup */}
      {deletingStatus && (
        <DeleteStatusPopup
          status={deletingStatus}
          allStatuses={statuses}
          onClose={() => setDeletingStatus(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Set Job Status Modal
// ---------------------------------------------------------------------------
export function SetJobStatusModal({ show, onClose, jobId, currentStatusId, onJobUpdated, excludeCancelled = false }) {
  const [selectedStatus,    setSelectedStatus]    = useState(null);
  const [showConfirmModal,  setShowConfirmModal]  = useState(false);
  const [confirmLoading,    setConfirmLoading]    = useState(false);
  const [confirmState,      setConfirmState]      = useState("pending");
  const [statusOptions,     setStatusOptions]     = useState(FALLBACK_STATUSES);
  const [statusesLoading,   setStatusesLoading]   = useState(false);
  const [showManage,        setShowManage]        = useState(false);
  const [searchQuery,       setSearchQuery]       = useState("");
  const searchInputRef                            = useRef(null);
  const [hasStatusChanges,  setHasStatusChanges]  = useState(false);
  const [showUpdateLeadStage, setShowUpdateLeadStage] = useState(false);
  const [pendingLeadId,       setPendingLeadId]       = useState(null);
  const [pendingLeadTitle,    setPendingLeadTitle]    = useState(null);
  const [pendingRevertStatusId, setPendingRevertStatusId] = useState(null);
  // jobId is a prop that goes stale (undefined) once the parent clears its
  // selected job in response to the onClose() below - capture it now so the
  // later revert (possibly minutes later, after the user interacts with the
  // Update Lead Stage modal) still targets the right job.
  const [pendingRevertJobId, setPendingRevertJobId] = useState(null);

  const [showCancelWarning, setShowCancelWarning] = useState(false);

  const handleModalClose = () => {
    if (hasStatusChanges) {
      window.location.reload();
    } else if (excludeCancelled) {
      setShowCancelWarning(true);
    } else {
      onClose();
    }
  };

  // Load statuses from API
  const loadStatuses = async (silent = false) => {
    if (!silent) setStatusesLoading(true);
    try {
      const res = await getJobStatuses();
      if (res.success && Array.isArray(res.data) && res.data.length > 0) {
        setStatusOptions(
          res.data.map((s) => ({
            value: s.StatusId,
            label: s.StatusName,
            alias: s.Alias || s.StatusName || "",
            type:  s.IsSystem ? "System" : "Custom",
            jobCount: s.JobCount || 0,
          }))
        );
      }
    } catch { /* fall back silently */ }
    finally { if (!silent) setStatusesLoading(false); }
  };

  useEffect(() => {
    if (show) {
      loadStatuses();
      setSearchQuery(""); // Reset search query on open
      setShowCancelWarning(false); // Reset warning state on open
      if (typeof window !== "undefined" && window.innerWidth >= 768) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 350); // Delay slightly to allow for modal transition
      }
    }
  }, [show]);

  useEffect(() => {
    if (show && currentStatusId) {
      const currentOpt = statusOptions.find((o) => o.value === Number(currentStatusId));
      const currentName = (currentOpt?.label || currentOpt?.alias || "").toLowerCase();
      if (excludeCancelled && currentName === "cancelled") {
        setSelectedStatus(null);
      } else {
        setSelectedStatus(Number(currentStatusId));
      }
    }
  }, [show, currentStatusId, statusOptions, excludeCancelled]);

  const handleSetStatusClick = () => {
    if (selectedStatus == null) {
      toast.error("Please select a status.");
      return;
    }
    const selectedOption = statusOptions.find((o) => o.value === selectedStatus);
    const selectedStatusName = (selectedOption?.alias || selectedOption?.label || "").toLowerCase();
    if (excludeCancelled && selectedStatusName === "cancelled") {
      toast.error("Please select a new job status.");
      return;
    }
    if (selectedStatus === Number(currentStatusId)) {
      toast.info("Status is already set to this value.");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmUpdate = async () => {
    if (!jobId) return;
    const previousStatusId = Number(currentStatusId);
    setConfirmLoading(true);
    setConfirmState("pending");
    try {
      const res = await updateJobManualStatus(jobId, selectedStatus);
      if (res.success) {
        toast.success("Job status updated successfully");
        setShowConfirmModal(false);
        setShowCancelWarning(false);
        
        if (hasStatusChanges) {
          window.location.reload();
        } else {
          if (onJobUpdated) onJobUpdated(jobId, selectedStatus);
          if (!excludeCancelled || !onJobUpdated) {
            onClose();
          }
        }

        // If the job was just cancelled, take the lead's new stage from
        // the user now - mirroring the Convert-to-Job flow (where backing
        // out reverts the lead's stage), backing out here instead reverts
        // this status change since a job can't be left Cancelled with its
        // lead still sitting in a job stage.
        const chosenOption = statusOptions.find((o) => o.value === selectedStatus);
        const chosenStatusName = (chosenOption?.label || chosenOption?.alias || "").toLowerCase();
        if (chosenStatusName === "cancelled" || chosenStatusName === "cancel") {
          const jobRes = await getJobById(jobId);
          let jobDetails = jobRes?.data?.data || jobRes?.data || {};
          if (typeof jobDetails === "string") {
            try { jobDetails = JSON.parse(jobDetails); } catch { /* ignore */ }
          }
          if (Array.isArray(jobDetails) && jobDetails.length > 0) jobDetails = jobDetails[0];
          const leadIdForJob = jobDetails?.Job?.LeadId || jobDetails?.LeadId || null;
          const leadTitleForJob = jobDetails?.Job?.Title || jobDetails?.Title || jobDetails?.JobTitle || jobDetails?.Job?.Name || null;
          if (leadIdForJob) {
            setPendingLeadId(leadIdForJob);
            setPendingLeadTitle(leadTitleForJob);
            setPendingRevertStatusId(previousStatusId);
            setPendingRevertJobId(jobId);
            setShowUpdateLeadStage(true);
          }
        }
      } else {
        setConfirmState("error");
        toast.error(res.message || "Failed to update job status");
      }
    } catch (error) {
      console.error(error);
      setConfirmState("error");
      toast.error("Error updating job status");
    } finally {
      setConfirmLoading(false);
    }
  };

  // If the user backs out of picking a lead stage after cancelling the
  // job, revert the job status back to what it was before.
  const handleRevertJobStatus = async () => {
    if (!pendingRevertJobId || pendingRevertStatusId == null) return;
    const revertRes = await updateJobManualStatus(pendingRevertJobId, pendingRevertStatusId);
    if (revertRes.success) {
      toast.info("Job status reverted since no lead stage was selected.");
      if (onJobUpdated) onJobUpdated(pendingRevertJobId, pendingRevertStatusId);
    } else {
      toast.error(revertRes.message || "Failed to revert job status");
    }
  };

  const selectedOption = statusOptions.find((o) => o.value === selectedStatus);
  const selectedStatusName = selectedOption?.alias || selectedOption?.label || "Open";
  const revertStatusOption = statusOptions.find((o) => o.value === pendingRevertStatusId);
  const revertStatusName = revertStatusOption?.alias || revertStatusOption?.label || "its previous status";

  const confirmMessages = {
    pending: {
      title: "Confirm Status Change",
      description: `Are you sure you want to change the status of this job to "${selectedStatusName}"?`,
      actionText: "Confirm",
    },
    success: {
      title: "Status Updated",
      description: `Job status has been successfully updated to "${selectedStatusName}".`,
      actionText: "Done",
    },
    error: {
      title: "Update Failed",
      description: "Failed to update the job status. Please check your connection and try again.",
      actionText: "Retry",
    },
  };

  return (
    <>
      {/* ── Main Modal ── */}
      <Transition appear show={show} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[9000] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
          onClose={() => {}}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200"  leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 transition-opacity dark:bg-black/40" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className={`scrollbar-sm relative flex flex-col rounded-2xl bg-white dark:bg-dark-700 overflow-hidden shadow-xl text-left transition-all duration-300 ${showManage ? "w-full max-w-2xl" : "w-full max-w-md"}`}>

              {showManage ? (
                <ManageStatusContent
                  onClose={() => setShowManage(false)}
                  statuses={statusOptions}
                  setStatuses={setStatusOptions}
                  reloadStatuses={(silent) => {
                    setHasStatusChanges(true);
                    loadStatuses(silent);
                  }}
                />
              ) : (
                <>
                  {/* ── Header ── */}
                  <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-600 px-5 py-4 bg-gray-50 dark:bg-dark-800">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/20">
                        <svg className="w-4 h-4 text-primary dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <DialogTitle as="h3" className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                        Set Job Status
                      </DialogTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Manage Button */}
                      {canManageJobStatus() && (
                        <button
                          onClick={() => setShowManage(true)}
                          className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-dark-500 bg-white dark:bg-dark-700 px-2.5 py-1.5 text-xs font-semibold text-gray-600 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-primary/40 hover:text-primary dark:hover:text-primary-400 active:scale-95 transition-all shadow-sm"
                        >
                          <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
                          Manage
                        </button>
                      )}
                      <button
                        onClick={handleModalClose}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-dark-600 dark:hover:text-white transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

              {/* ── Body ── */}
              <div className="px-5 py-5 space-y-4 overflow-y-auto max-h-[60vh] scrollbar-sm">
                <p className="text-xs text-gray-500 dark:text-dark-300 leading-normal shrink-0">
                  Set the manual status level for this job to help organize and track progress.
                </p>

                {/* Search Bar */}
                <div className="relative shrink-0">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by alias name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full rounded-lg border border-gray-200 dark:border-dark-500 bg-white dark:bg-dark-600 py-2 pl-9 pr-3 text-sm text-gray-900 dark:text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                  />
                </div>

                <div className="space-y-2.5">
                  {statusesLoading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="h-12 rounded-xl bg-gray-100 dark:bg-dark-600 animate-pulse" />
                    ))
                  ) : (
                    statusOptions
                      .filter((opt) => {
                        const name = (opt.alias || opt.label || "").toLowerCase();
                        if (excludeCancelled && name === "cancelled") return false;
                        return name.includes(searchQuery.toLowerCase());
                      })
                      .map((opt) => {
                      const isChecked = selectedStatus === opt.value;
                      return (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-3 p-3.5 border rounded-xl cursor-pointer transition-all ${
                            isChecked
                              ? "border-primary-600 bg-primary-50/10 dark:border-primary-400 dark:bg-primary-950/10"
                              : "border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-800"
                          }`}
                        >
                          <input
                            type="radio"
                            name="jobStatus"
                            value={opt.value}
                            checked={isChecked}
                            onChange={() => setSelectedStatus(opt.value)}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 dark:border-dark-600 cursor-pointer"
                          />
                          <span className="flex-1 text-sm font-bold text-gray-800 dark:text-dark-100">{opt.alias || opt.label}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              {/* ── Footer ── */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-100 dark:border-dark-600 px-5 py-4 bg-gray-50/50 dark:bg-dark-800/50">
                <Button onClick={handleModalClose} variant="outlined" className="px-4 py-2 text-xs font-bold">Cancel</Button>
                <Button onClick={handleSetStatusClick} color="primary" className="px-4 py-2 text-xs font-bold">Set Status</Button>
              </div>
                </>
              )}
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* ── Confirm status change ── */}
      <ConfirmModal
        show={showConfirmModal}
        onClose={() => { setShowConfirmModal(false); setConfirmState("pending"); }}
        onOk={handleConfirmUpdate}
        confirmLoading={confirmLoading}
        state={confirmState}
        messages={confirmMessages}
        buttonColor="primary"
      />

      {/* ── Job was cancelled: take the lead's new stage from the user,
           mirroring Convert-to-Job's mandatory pattern ── */}
      <UpdateLeadStageModal
        show={showUpdateLeadStage}
        onClose={() => setShowUpdateLeadStage(false)}
        leadId={pendingLeadId}
        leadTitle={pendingLeadTitle}
        title="Update Lead Stage"
        description="This job's status has been set to Cancelled. Select the sales stage the associated lead should move to."
        submitLabel="Update Stage"
        confirmTitle="Update Lead Stage?"
        confirmDescription="Are you sure you want to update the lead's stage?"
        confirmActionText="Yes, Update Stage"
        onSuccess={() => setShowUpdateLeadStage(false)}
        onCancel={handleRevertJobStatus}
        cancelWarningTitle="Cancel Lead Stage Update?"
        cancelWarningDescription={`If you continue, the job's status will be reverted back to "${revertStatusName}" since a new lead stage wasn't selected.`}
        cancelWarningActionText="Yes, Revert Status"
      />

      {/* ── Warning modal for reactivation cancel ── */}
      <Transition appear show={showCancelWarning} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setShowCancelWarning(false)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 transition-opacity dark:bg-black/40" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="scrollbar-sm dark:bg-dark-700 relative flex w-full max-w-md flex-col overflow-y-auto rounded-lg bg-white px-4 py-4 text-center transition-opacity duration-300 sm:px-5 sm:py-5">
                  <div className="flex justify-center mb-4">
                    <ExclamationTriangleIcon className="mx-auto size-16 shrink-0 text-warning" />
                  </div>
                  <DialogTitle as="h3" className="dark:text-dark-100 text-lg sm:text-xl font-semibold text-gray-800">
                    Cancel Job Reactivation?
                  </DialogTitle>
                  <div className="mx-auto mt-1.5 max-w-sm text-xs sm:text-sm text-gray-500 dark:text-dark-300">
                    If you continue, the Lead will be moved back to its previous stage and the stage change will be reverted. Any unfinished reactivation progress will be discarded.
                  </div>

                  <div className="mt-5 flex justify-center gap-3">
                    <Button variant="outlined" className="h-9 min-w-[7rem]" onClick={() => setShowCancelWarning(false)}>
                      Cancel
                    </Button>
                    <Button variant="filled" color="primary" className="h-9 min-w-[7rem]" onClick={() => {
                      setShowCancelWarning(false);
                      onClose();
                    }}>
                      Yes, Revert Stage
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
